// src/App.js
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import './App.css';
import MyHeader from './comps/MyHeader';
import MyMenu from './comps/MyMenu';
import AppRoutes from './AppRoutes';
import MyFooter from './comps/MyFooter';
import SyncOverlay from './comps/SyncOverlay';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AutoLogout from './comps/AutoLogout';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';

import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import { useNetworkStatusWithHistory } from './utils/networkDetector';
import syncService from './services/syncService';
import { getCurrentAPI, forceServerCheck, isUsingLocalServer as checkIsLocal, switchToOnline, switchToLocal } from './services/api';
import AppConst from './apis/appconst';

function App() {
  const location = useLocation();

  const pathname = location?.pathname || window.location.pathname;
  const lastSegment = pathname.replace(/\/+$/, "").split("/").pop() || "";
  const hasSession = !!sessionStorage.getItem("ldata");
  const publicRoutes = new Set(["login", "lab_report"]);
  const isPublic = publicRoutes.has(lastSegment.toLowerCase());
  const isLogin = !isPublic && hasSession;

  // Use enhanced network hook with transition tracking
  const { isOnline, wasOffline, transition, clearWasOffline } = useNetworkStatusWithHistory();
  const [isSlow, setIsSlow] = useState(false);

  // Server tracking
  const [currentServer, setCurrentServer] = useState('Checking...');
  const [isUsingLocalServer, setIsUsingLocalServer] = useState(false);
  const [syncStatus, setSyncStatus] = useState('');
  const [pendingSync, setPendingSync] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);

  // Sync overlay state
  const [showSyncOverlay, setShowSyncOverlay] = useState(false);
  const [syncOverlayMessage, setSyncOverlayMessage] = useState('');

  const [updateReady, setUpdateReady] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);

  // Track previous states
  const wasUsingLocal = useRef(false);

  // ---------- SERVICE WORKER ----------
  useEffect(() => {
    const setUpdate = (worker, source = "unknown") => {
      setWaitingWorker(worker || null);
      setUpdateReady(true);
      toast.info("⬆️ New app version available — click Update in header");
    };

    try {
      if (serviceWorkerRegistration && typeof serviceWorkerRegistration.register === "function") {
        serviceWorkerRegistration.register({
          onUpdate: (registration) => {
            setUpdate(registration.waiting, "serviceWorkerRegistration.onUpdate");
          },
          onSuccess: (reg) => {
            console.log("[App] serviceWorkerRegistration.onSuccess", reg);
          },
        });
      }
    } catch (err) {
      console.warn("[App] serviceWorkerRegistration.register threw:", err);
    }

    const checkWaiting = async () => {
      try {
        if (!('serviceWorker' in navigator)) return;
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg && reg.waiting) {
          setUpdate(reg.waiting, "navigator.getRegistration");
        }
      } catch (err) {}
    };

    navigator.serviceWorker?.addEventListener?.("message", () => {});

    let pollInterval = null;
    (async () => {
      await checkWaiting();
      let tries = 0;
      pollInterval = setInterval(async () => {
        tries++;
        await checkWaiting();
        if (tries >= 12) {
          clearInterval(pollInterval);
          pollInterval = null;
        }
      }, 3000);
    })();

    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, []);

  const refreshApp = () => {
    if (!waitingWorker) {
      window.location.reload(true);
      return;
    }
    const onControllerChange = () => {
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);
    try {
      waitingWorker.postMessage({ type: "SKIP_WAITING" });
      setTimeout(() => {
        try {
          navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
        } catch (e) {}
      }, 8000);
    } catch (err) {
      window.location.reload(true);
    }
  };

  // ======================================================
  // CORE: Handle network transitions with bidirectional sync
  // No manual refresh needed — this runs automatically
  // ======================================================
  const detectAndSync = useCallback(async () => {
    // Force fresh server check
    forceServerCheck();
    const api = await getCurrentAPI();
    const usingLocal = api === AppConst.LOCAL_URL;

    setIsUsingLocalServer(usingLocal);
    setCurrentServer(usingLocal ? 'Local Server (Offline Mode)' : 'Online Server');

    console.log('[App] Network change detected. Online:', isOnline, 'API:', api, 'Transition:', transition);

    // ============================================
    // CASE 1: Offline → Online
    // Sync local pending data TO online server
    // Then switch to online server
    // ============================================
    if (isOnline && (wasOffline || wasUsingLocal.current)) {
      console.log('[App] Transition: Offline → Online. Syncing local → online...');

      const pendingCount = await syncService.getPendingCount();

      if (pendingCount > 0) {
        setShowSyncOverlay(true);
        setSyncOverlayMessage(`Syncing ${pendingCount} offline records to server...`);
        setPendingSync(pendingCount);
        setIsSyncing(true);
        setSyncStatus('Syncing offline data...');

        try {
          const result = await syncService.manualSync();

          if (result?.synced) {
            toast.success(`✅ ${pendingCount} offline records synced successfully!`);
            setSyncStatus('✓ Synced');
            setPendingSync(0);
          } else {
            toast.warning("⚠️ Some records may not have synced. Will retry.");
            setSyncStatus('Partial sync');
          }
        } catch (error) {
          console.error('[App] Transition sync failed:', error);
          toast.error("Sync failed - will retry in background");
          setSyncStatus('✗ Sync failed - retrying...');
        } finally {
          setIsSyncing(false);
          setShowSyncOverlay(false);
          setSyncOverlayMessage('');
        }
      }

      // Switch API to online
      switchToOnline();
      setIsUsingLocalServer(false);
      setCurrentServer('Online Server');
      AppConst.BASE_URL = AppConst.ONLINE_URL;

      // Clear flags
      clearWasOffline();
      wasUsingLocal.current = false;

      // DISPATCH REFRESH EVENT — child components auto-reload data
      // No manual page refresh needed!
      window.dispatchEvent(new CustomEvent('data-refresh', {
        detail: { reason: 'offline-to-online', timestamp: Date.now() }
      }));

      console.log('[App] Switched to online. Dispatched data-refresh event.');
    }

    // ============================================
    // CASE 2: Online → Offline
    // Backup online data TO local server first
    // Then switch to local server
    // ============================================
    if (!isOnline && transition === 'online-to-offline') {
      console.log('[App] Transition: Online → Offline. Backing up online → local...');

      // Quick backup attempt before going fully offline
      setShowSyncOverlay(true);
      setSyncOverlayMessage('Saving latest data to local server...');

      try {
        const backupResult = await syncService.performBackup();
        if (backupResult?.backed_up) {
          toast.info("📥 Latest data backed up to local server");
        }
      } catch (error) {
        console.warn('[App] Online → Offline backup failed (may already be offline):', error.message);
      } finally {
        setShowSyncOverlay(false);
        setSyncOverlayMessage('');
      }

      // Switch to local
      switchToLocal();
      setIsUsingLocalServer(true);
      setCurrentServer('Local Server (Offline Mode)');
      wasUsingLocal.current = true;
      AppConst.BASE_URL = AppConst.LOCAL_URL;

      // DISPATCH REFRESH EVENT — child components reload from local
      window.dispatchEvent(new CustomEvent('data-refresh', {
        detail: { reason: 'online-to-offline', timestamp: Date.now() }
      }));

      console.log('[App] Switched to local. Dispatched data-refresh event.');
    }

    // ============================================
    // CASE 3: Already offline, staying offline
    // ============================================
    if (!isOnline && transition !== 'online-to-offline') {
      wasUsingLocal.current = true;
      AppConst.BASE_URL = AppConst.LOCAL_URL;
    }

  }, [isOnline, wasOffline, transition, clearWasOffline]);

  // Run detectAndSync whenever network status changes
  useEffect(() => {
    detectAndSync();
  }, [isOnline, detectAndSync]);

  // ======================================================
  // BACKGROUND AUTO-SYNC + AUTO-BACKUP (bidirectional)
  // When online: sync local→online AND backup online→local
  // ======================================================
  useEffect(() => {
    if (isOnline && isUsingLocalServer) {
      // Online but using local server: sync local → online
      console.log("[App] Online + Local server → Starting auto-sync (local → online)");
      setSyncStatus('Auto-sync active');

      syncService.startAutoSync(
        (data) => {
          setSyncStatus('✓ Synced');
          setPendingSync(0);
          toast.success("📤 Data synced to online server");
          setTimeout(() => setSyncStatus('Auto-sync active'), 3000);
        },
        (error) => {
          if (error.message?.includes('local_server_offline')) {
            setSyncStatus('⚠️ Local server offline');
          } else {
            setSyncStatus('✗ Sync failed');
            setTimeout(() => setSyncStatus('Auto-sync active'), 5000);
          }
        },
        (pendingCount) => {
          setPendingSync(pendingCount);
          setSyncStatus(`Syncing ${pendingCount} records...`);
        }
      );

      const checkPending = async () => {
        const count = await syncService.getPendingCount();
        setPendingSync(count);
      };
      checkPending();
      const pendingInterval = setInterval(checkPending, 15000);

      return () => {
        syncService.stopAutoSync();
        clearInterval(pendingInterval);
      };

    } else if (isOnline && !isUsingLocalServer) {
      // =============================================
      // KEY NEW FEATURE: Online + Online server
      // Auto-backup online → local every 30 seconds
      // So local DB always has latest data
      // =============================================
      console.log("[App] Online + Online server → Starting auto-backup (online → local)");
      setSyncStatus('Online - Auto-backup active');
      setPendingSync(0);

      syncService.startAutoBackup(
        (data) => {
          console.log('[App] Auto-backup completed:', data);
          // Silent — don't toast for routine backups
        },
        (error) => {
          // Silent — local server might not be running, that's OK
          console.warn('[App] Auto-backup failed:', error.message);
        }
      );

      return () => {
        syncService.stopAutoBackup();
      };

    } else {
      // Offline
      syncService.stopAll();
      setSyncStatus('Offline - working locally');
    }
  }, [isOnline, isUsingLocalServer]);

  // Manual sync handler
  const handleManualSync = async () => {
    if (!isOnline) {
      toast.warning("Cannot sync while offline");
      return;
    }

    setIsSyncing(true);
    setShowSyncOverlay(true);
    setSyncOverlayMessage('Manual sync in progress...');

    try {
      // Bidirectional sync
      const result = await syncService.fullSync();

      if (result.sync?.synced) {
        toast.success("✓ Local → Online sync completed");
      }
      if (result.backup?.backed_up) {
        toast.success("✓ Online → Local backup completed");
      }

      setSyncStatus('✓ Fully synced');
      setPendingSync(0);

      // Refresh child components
      window.dispatchEvent(new CustomEvent('data-refresh', {
        detail: { reason: 'manual-sync', timestamp: Date.now() }
      }));

    } catch (error) {
      toast.error("Sync failed: " + error.message);
      setSyncStatus('✗ Sync failed');
    } finally {
      setIsSyncing(false);
      setShowSyncOverlay(false);
      setSyncOverlayMessage('');
      setTimeout(() => setSyncStatus(isUsingLocalServer ? 'Auto-sync active' : 'Online - Auto-backup active'), 3000);
    }
  };

  // ---------- SPEED CHECK ----------
  useEffect(() => {
    let isMounted = true;
    const checkSpeed = async () => {
      if (!navigator.onLine) { if (isMounted) setIsSlow(false); return; }
      const start = performance.now();
      try {
        await fetch("/favicon.ico", { cache: "no-cache", method: "GET" });
        const duration = performance.now() - start;
        if (!isMounted) return;
        if (duration > 2000) {
          if (!isSlow) toast.warning("⚠️ Slow Internet connection");
          setIsSlow(true);
        } else {
          setIsSlow(false);
        }
      } catch (err) {
        if (isMounted) setIsSlow(false);
      }
    };
    checkSpeed();
    const interval = setInterval(checkSpeed, 10000);
    return () => { isMounted = false; clearInterval(interval); };
  }, [isSlow]);

  // ---------- MOBILE LAYOUT ----------
  useEffect(() => {
    const fixMobileSpacing = () => {
      if (window.innerWidth < 992) {
        document.body.style.marginLeft = '0';
        document.body.style.paddingLeft = '0';
      }
    };
    fixMobileSpacing();
    window.addEventListener('resize', fixMobileSpacing);
    return () => window.removeEventListener('resize', fixMobileSpacing);
  }, []);

  // ---------- STYLES ----------
  const bannerStyleBase = {
    position: "fixed", top: 0, left: 0, right: 0,
    zIndex: 9998, textAlign: "center",
    padding: "8px 12px", fontWeight: "600", fontSize: "14px",
  };

  const syncBarStyle = {
    position: "fixed", bottom: 10, left: "50%", transform: "translateX(-50%)",
    zIndex: 9997,
    background: isOnline
      ? (isUsingLocalServer ? "#17a2b8" : "#28a745")
      : "#6c757d",
    color: "#fff", padding: "6px 16px",
    display: "inline-flex", alignItems: "center", gap: "12px",
    fontSize: "13px", fontWeight: "500",
    borderRadius: "20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
    whiteSpace: "nowrap",
  };

  const getMainContentStyle = () => {
    const isMobile = window.innerWidth < 992;
    const topBannerHeight = !isOnline || (isOnline && isSlow) ? 48 : 0;
    const bottomBarHeight = 36;
    if (isMobile) {
      return {
        marginLeft: "0px",
        paddingTop: `${topBannerHeight + 8}px`,
        paddingBottom: `${bottomBarHeight + 8}px`,
        marginTop: "0px"
      };
    }
    return {
      paddingTop: `${topBannerHeight + 8}px`,
      paddingBottom: `${bottomBarHeight + 8}px`,
      marginTop: "0px"
    };
  };

  return (
    <div id="layout-wrapper">
      <ToastContainer
        position="top-right" autoClose={3000}
        hideProgressBar={false} newestOnTop closeOnClick pauseOnHover draggable
      />

      {/* SYNC OVERLAY - blocks UI during transitions */}
      <SyncOverlay
        visible={showSyncOverlay}
        pendingCount={pendingSync}
        message={syncOverlayMessage}
      />

      {!isOnline && (
        <div style={{ ...bannerStyleBase, background: "#d9534f", color: "#fff" }}>
          🚫 No Internet — Working in Offline Mode (data saves locally)
        </div>
      )}

      {isOnline && isSlow && (
        <div style={{ ...bannerStyleBase, background: "#f0ad4e", color: "#fff" }}>
          ⚠️ Slow Internet Connection
        </div>
      )}

      {/* Status Bar */}
      {isLogin && (
        <div style={syncBarStyle}>
          <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
            <span>
              <strong>{isOnline ? '🟢' : '🔴'}</strong> {currentServer}
            </span>
            <span style={{ fontSize: "12px", opacity: 0.9 }}>
              {syncStatus}
            </span>
            {pendingSync > 0 && (
              <span style={{
                background: "rgba(255,255,255,0.2)",
                padding: "2px 8px", borderRadius: "10px", fontSize: "12px"
              }}>
                {pendingSync} pending
              </span>
            )}
          </div>
          {isOnline && (
            <button
              onClick={handleManualSync}
              disabled={isSyncing}
              style={{
                padding: "4px 12px",
                background: "rgba(255,255,255,0.9)",
                color: isUsingLocalServer ? "#17a2b8" : "#28a745",
                border: "none", borderRadius: "4px",
                cursor: isSyncing ? "not-allowed" : "pointer",
                fontSize: "12px", fontWeight: "600",
                opacity: isSyncing ? 0.6 : 1
              }}
            >
              {isSyncing ? "Syncing..." : "Sync Now"}
            </button>
          )}
        </div>
      )}

      <AutoLogout timeoutMs={2 * 60 * 60 * 1000} redirectPath="/login" />

      {isLogin ? <MyHeader updateReady={updateReady} refreshApp={refreshApp} /> : null}
      {isLogin ? <MyMenu /> : null}

      <div className="vertical-overlay"></div>

      <div className="main-content" style={getMainContentStyle()}>
        <AppRoutes />
        {isLogin ? <MyFooter /> : null}
      </div>
    </div>
  );
}

export default App;